import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase } from 'lucide-react';
import { ProjectLinkType } from '../../firebase/projectService';
import { PLATFORM_META } from '../../utils/projectLinks';
import ProjectImage from '../ProjectImage';

interface ProjectCardProps {
  title: string;
  description: string;
  imageUrl: string;
  tags: string[];
  link?: string; // Internal route to the detail page (NOT an external live link)
  platforms?: ProjectLinkType[]; // Live-link platforms this project is available on
  role?: string; // e.g. "Lead Full-Stack Developer" — highlighted for recruiters
  company?: string; // company / client name
  projectType?: 'personal' | 'company' | 'freelance' | 'open-source';
}

const TYPE_LABELS: Record<NonNullable<ProjectCardProps['projectType']>, string> = {
  company: 'Company',
  freelance: 'Freelance',
  'open-source': 'Open Source',
  personal: 'Personal',
};

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  description,
  imageUrl,
  tags,
  link,
  platforms = [],
  role,
  company,
  projectType,
}) => {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white dark:bg-gray-800 ring-1 ring-gray-200/70 dark:ring-gray-700/70 shadow-sm hover:shadow-xl hover:ring-primary-300 dark:hover:ring-primary-700 transition-shadow duration-300"
    >
      {/* Image header */}
      <div className="relative h-52 flex-shrink-0 overflow-hidden">
        <ProjectImage
          src={imageUrl}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        />
        {/* Gradient for depth & legibility of badges */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-90" />

        {/* Project type badge */}
        {projectType && (
          <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-white/95 dark:bg-gray-900/90 px-2.5 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 shadow-sm ring-1 ring-black/5 backdrop-blur-sm">
            {TYPE_LABELS[projectType]}
          </span>
        )}

        {/* Platform availability badges */}
        {platforms.length > 0 && (
          <div className="absolute top-3 right-3 flex gap-1.5">
            {platforms.map((type) => {
              const meta = PLATFORM_META[type];
              const Icon = meta.Icon;
              return (
                <span
                  key={type}
                  title={meta.label}
                  style={{ color: meta.color }}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-white/95 dark:bg-gray-900/90 shadow-sm backdrop-blur-sm ring-1 ring-black/5"
                >
                  <Icon className="h-4 w-4" />
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-grow flex-col p-6">
        <h3 className="mb-1.5 text-lg font-bold leading-snug text-gray-900 dark:text-white line-clamp-2 transition-colors group-hover:text-primary-600 dark:group-hover:text-primary-400">
          {title}
        </h3>

        {/* Recruiter context: role @ company */}
        {(role || company) && (
          <p className="mb-3 flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400">
            <Briefcase className="h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1">
              {role}
              {role && company && <span className="text-gray-400 dark:text-gray-500"> · </span>}
              {company && <span className="text-gray-600 dark:text-gray-400">{company}</span>}
            </span>
          </p>
        )}

        <p className="mb-4 flex-grow text-sm leading-relaxed text-gray-600 dark:text-gray-400 line-clamp-3">
          {description}
        </p>

        {/* Tags */}
        <div className="mb-5 flex flex-wrap gap-2">
          {tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="rounded-full bg-primary-50 dark:bg-primary-900/25 px-2.5 py-1 text-xs font-medium text-primary-700 dark:text-primary-300"
            >
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400">
              +{tags.length - 3}
            </span>
          )}
        </div>

        {/* CTA pinned to bottom */}
        {link && (
          <div className="mt-auto">
            <Link
              to={link}
              className="group/btn inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:bg-primary-700 hover:shadow-md"
            >
              View Project
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProjectCard;
